import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo
} from 'react';
import styled from 'styled-components';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { config } from 'config';

import { ReactComponent as AVSmallLogo } from 'images/logo-small.svg';
import { ReactComponent as ECOSULogo } from 'images/ecosu-login.svg';
import { ReactComponent as BgAV } from 'images/BG-av.svg';
import { ReactComponent as BgECOSU } from 'images/BG-ecosu.svg';
import { ReactComponent as WodomatLogo } from 'images/wodoma-small.svg';
import { ReactComponent as BgWodomat } from 'images/BG-wodomat.svg';
import { BRANDS, withBrand } from 'hooks/useBrand';

const LOGOS = {
  [BRANDS.AV]: {
    component: AVSmallLogo,
    bg: BgAV,
    color: '#ededed'
  },
  [BRANDS.ECOSU]: {
    component: ECOSULogo,
    bg: BgECOSU,
    color: '#fff'
  },
  [BRANDS.WODOMAT]: {
    component: WodomatLogo,
    bg: BgWodomat,
    color: '#ededed'
  }
};

const LOGIN_STAGE_BADGES = {
  'pg-perm': 'PROD cluster, тестовый контур, аутентификация postgres',
  'kk-perm': 'PROD cluster, тестовый контур, аутентификация keycloak'
};

export const LoginContext = createContext({
  title: undefined,
  setTitle: () => {}
});

function Login({ className, children, brand }) {
  const { title } = useContext(LoginContext);
  const Logo = useMemo(() => LOGOS[brand].component, [brand]);
  const Bg = useMemo(() => LOGOS[brand].bg, [brand]);
  const stageBadge = LOGIN_STAGE_BADGES[config.STAGE];
  const { t } = useTranslation('login');
  return (
    <div className={cn(className, brand)}>
      <header>
        <div>
          <Logo height="auto" />
          <span>{title || t('title.login')}</span>
        </div>
      </header>
      <main>{children}</main>
      <Bg className="bg" />
      {stageBadge ? <div className="stageBadge">{stageBadge}</div> : null}
      <div className="version">v{config.npm_package_version}</div>
    </div>
  );
}

const styledLogin = styled(Login)`
  flex-direction: column;
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  header {
    width: 100%;
    min-height: 90px;
    background: ${({ brand }) => LOGOS[brand].color};
    padding: 24px 0 5px;
    &,
    & > div {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    > div {
      padding: 0 14px;
    }

    span {
      margin-top: 6px;
    }
    svg {
      width: 27px;
    }
  }

  &.ECOSU {
    header {
      svg {
        width: 100%;
      }
    }
  }

  header > div,
  main {
    z-index: 2;
    width: 100%;
    max-width: 400px;
  }
  main {
    margin-top: 20px;
    padding: 0 14px;
  }
  .bg {
    width: 100vw;
    max-width: 366px;
    position: absolute;
    top: 300px;
    z-index: 1;
  }
  .version {
    position: fixed;
    bottom: 20px;
    width: 100%;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    z-index: 2;
  }
  .stageBadge {
    position: fixed;
    left: 50%;
    bottom: 48px;
    transform: translateX(-50%);
    width: calc(100% - 32px);
    max-width: 520px;
    padding: 10px 16px;
    border: 3px solid #8f949b;
    border-radius: 14px;
    background: #e3e5e8;
    color: #2f343b;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    text-align: center;
    z-index: 2;
  }
`;

const StyledLogin = withBrand(styledLogin);

export default function LoginContainer({ children }) {
  const [title, setTitle] = useState(undefined);
  return (
    <LoginContext.Provider value={{ title, setTitle }}>
      <StyledLogin>{children}</StyledLogin>
    </LoginContext.Provider>
  );
}

export function useSetLoginTitle(title) {
  const { setTitle } = useContext(LoginContext);
  useEffect(() => {
    setTitle(title);
  }, [setTitle, title]);
}
